<template>
  <div class="pls-receive-container">
    <!-- 页面头部 -->
    <header class="header">
      <el-button link class="back-btn" @click="handleBack" aria-label="返回">
        <img src="@/assets/返回.svg" alt="返回" class="icon-back" />
      </el-button>
      <h1 class="title">PLS扫码收货</h1>
    </header>

    <!-- Tab 切换 -->
    <el-segmented
      v-model="activeTab"
      :options="tabOptions"
      @change="handleTabChange"
      block
      class="tab-segment"
    />

    <main class="content">
      <!-- 扫GV单区域 -->
      <div v-if="activeTab === 'gv'">
        <!-- 扫描区域 -->
        <div class="scan-section">
          <div class="scan-box">
            <el-input
              ref="scanInput"
              v-model="receiptNo"
              placeholder="请扫描PDA单号"
              @input="handleScan"
              @keyup.enter="queryData"
              clearable
              @clear="clearInput"
              class="scan-input"
              size="large"
            >
              <template #prefix>
                <el-icon><Search /></el-icon>
              </template>
              <template #suffix>
                <el-button
                  link
                  class="scan-btn"
                  @click="startScan('gv')"
                  aria-label="扫码"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    width="20"
                    height="20"
                  >
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <path
                      d="M7 7h3v3H7zM14 7h3v3h-3zM7 14h3v3H7zM14 14h3v3h-3z"
                    />
                  </svg>
                </el-button>
              </template>
            </el-input>
          </div>

          <div class="scan-actions">
            <el-button
              type="primary"
              class="light-btn"
              round
              @click="queryData"
              :loading="isLoading"
            >
              {{ isLoading ? "查询中..." : "查询收货单" }}
            </el-button>
            <el-button round @click="clearInput">清空</el-button>
          </div>
        </div>

        <!-- 收货单信息 -->
        <div v-if="receiptData" class="receipt-info">
          <h3 class="section-title">收货单信息</h3>
          <div class="info-card">
            <div class="info-row">
              <span class="label">供应商：</span>
              <span class="value">{{ receiptData.supplierName }}</span>
            </div>
            <div class="info-row">
              <span class="label">PLS单号：</span>
              <span class="value">{{ receiptData.receiptNo }}</span>
            </div>
            <div class="info-row">
              <span class="label">入库单号：</span>
              <span class="value">{{ receiptData.wareInNo }}</span>
            </div>
            <div class="info-row">
              <span class="label">收货状态：</span>
              <el-tag
                :type="getStatusTagType(receiptData.wareInStatus)"
                size="small"
                round
              >
                {{ getStatusText(receiptData.wareInStatus) }}
              </el-tag>
            </div>
            <div
              v-if="String(receiptData.failDesc || '').trim()"
              class="info-row info-row-fail"
            >
              <span class="label">入库失败原因：</span>
              <span class="value value-fail">{{ receiptData.failDesc }}</span>
            </div>
          </div>
        </div>

        <!-- 物料列表 -->
        <div v-if="materialList.length > 0" class="material-section">
          <h3 class="section-title">物料信息</h3>
          <div class="material-list">
            <div
              v-for="(material, index) in materialList"
              :key="index"
              class="material-item"
            >
              <div class="material-header">
                <div class="material-no">{{ material.materialNo }}</div>
              </div>
              <div class="material-details">
                <div class="detail-row">
                  <span class="detail-label">物料描述：</span>
                  <span class="detail-value">{{ material.materialDesc }}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">收货数量：</span>
                  <span class="detail-value text-primary">{{
                    formatNumber(material.wareInNum)
                  }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 空状态 -->
        <el-empty
          v-if="!receiptNo && !receiptData"
          description="请扫描PDA单号开始收货"
          :image-size="80"
        />
      </div>

      <!-- 扫发运单区域 -->
      <div v-if="activeTab === 'send'" class="send-tab-content">
        <!-- 扫描区域 -->
        <div class="scan-section">
          <div class="scan-box">
            <el-input
              ref="sendScanInput"
              v-model="sendNo"
              placeholder="请扫描发运单号"
              @input="handleSendScan"
              @keyup.enter="querySendData"
              clearable
              @clear="clearSendInput"
              class="scan-input"
              size="large"
            >
              <template #prefix>
                <el-icon><Search /></el-icon>
              </template>
              <template #suffix>
                <el-button
                  link
                  class="scan-btn"
                  @click="startScan('send')"
                  aria-label="扫码"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    width="20"
                    height="20"
                  >
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <path
                      d="M7 7h3v3H7zM14 7h3v3h-3zM7 14h3v3H7zM14 14h3v3h-3z"
                    />
                  </svg>
                </el-button>
              </template>
            </el-input>
          </div>

          <div class="scan-actions">
            <el-button
              type="primary"
              class="light-btn"
              round
              @click="querySendData"
              :loading="isLoading"
            >
              {{ isLoading ? "查询中..." : "查询收货单" }}
            </el-button>
            <el-button round @click="clearSendInput">清空</el-button>
          </div>
        </div>

        <!-- GV单列表 -->
        <div v-if="gvList.length > 0" class="gv-list-section">
          <div v-if="gvList[0]?.supplierName" class="supplier-header">
            <span class="supplier-label">供应商：</span>
            <span class="supplier-name">{{ gvList[0].supplierName }}</span>
          </div>
          <div class="section-header sticky-header">
            <h3 class="section-title">GV单列表</h3>
            <div class="section-actions">
              <span class="select-hint"
                >已选择 {{ selectedGvList.length }} 个（数量：{{
                  formatNumber(selectedTotalQty)
                }}）</span
              >
              <el-button
                type="primary"
                text
                size="small"
                @click.stop="toggleSelectAll"
              >
                {{ isAllSelected ? "取消全选" : "全选" }}
              </el-button>
            </div>
          </div>
          <div class="gv-list-wrapper">
            <div class="gv-list">
              <div
                v-for="(item, index) in gvList"
                :key="item.id || item.wareInId || index"
                class="gv-item"
                @click="toggleGvSelect(item)"
              >
                <el-checkbox
                  :model-value="isGvSelected(item)"
                  @click.stop
                  @change="toggleGvSelect(item)"
                />
                <div class="gv-content">
                  <div class="gv-row">
                    <span class="gv-label">GV单号：</span>
                    <span class="gv-value">{{ item.receiptNo || "-" }}</span>
                  </div>
                  <div class="gv-row">
                    <span class="gv-label">物料编码：</span>
                    <span class="gv-value">{{ item.materialNo || "-" }}</span>
                  </div>
                  <div class="gv-row">
                    <span class="gv-label">数量：</span>
                    <span class="gv-value text-primary">{{
                      formatNumber(item.wareInNum)
                    }}</span>
                  </div>
                  <div
                    v-if="String(item.failDesc || '').trim()"
                    class="gv-row gv-row-fail"
                  >
                    <span class="gv-label">入库失败原因：</span>
                    <span class="gv-value gv-value-fail">{{
                      item.failDesc
                    }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 空状态 -->
        <el-empty
          v-if="!sendNo && gvList.length === 0"
          description="请扫描发运单号开始收货"
          :image-size="80"
        />

        <!-- 未找到状态 -->
        <el-empty
          v-if="sendNo && gvList.length === 0 && !isLoading"
          description="未找到相关GV单"
          :image-size="80"
        />
      </div>
    </main>

    <!-- 固定底部：开始收货按钮 -->
    <div
      v-if="
        (activeTab === 'gv' && receiptData && receiptData.wareInStatus === 1) ||
        (activeTab === 'send' && selectedGvList.length > 0)
      "
      class="fixed-bottom-action"
    >
      <el-button
        type="success"
        size="large"
        round
        class="start-btn"
        :loading="isSubmitting"
        :disabled="isSubmitting"
        @click="startReceipt"
      >
        {{ isSubmitting ? "加载中..." : "进行收货" }}
      </el-button>
    </div>

    <!-- 扫码组件 -->
    <CameraScan
      v-model:visible="scanVisible"
      :title="activeTab === 'gv' ? '扫描GV单' : '扫描发运单'"
      @success="handleScanSuccess"
      @close="scanVisible = false"
    />
  </div>
</template>

<script>
import { ref, onMounted, nextTick, computed } from "vue";
import { useRouter, useRoute, onBeforeRouteLeave } from "vue-router";
import { Search } from "@element-plus/icons-vue";
import toast from "@/utils/toast";
import request from "@/utils/request";
import CameraScan from "@/components/CameraScan";

const PLS_RECEIVE_CACHE_KEY = "pls-receive-scan-cache";

export default {
  name: "PLSReceive",
  components: {
    CameraScan,
    Search,
  },
  setup() {
    const router = useRouter();
    const route = useRoute();

    const activeTab = ref("send"); // 'gv' | 'send'
    const tabOptions = [
      { label: "扫发运单", value: "send" },
      { label: "扫GV单", value: "gv" },
    ];
    const handleTabChange = (val) => {
      switchTab(val);
    };
    const receiptNo = ref("");
    const receiptData = ref(null);
    const materialList = ref([]);
    const sendNo = ref("");
    const gvList = ref([]);
    const selectedGvList = ref([]);
    const isLoading = ref(false);
    const isSubmitting = ref(false);
    const scanInput = ref(null);
    const sendScanInput = ref(null);
    const scanVisible = ref(false);

    const saveScanCache = () => {
      try {
        sessionStorage.setItem(
          PLS_RECEIVE_CACHE_KEY,
          JSON.stringify({
            activeTab: activeTab.value,
            receiptNo: String(receiptNo.value || "").trim(),
            sendNo: String(sendNo.value || "").trim(),
          }),
        );
      } catch {
        // ignore
      }
    };

    const clearScanCache = () => {
      try {
        sessionStorage.removeItem(PLS_RECEIVE_CACHE_KEY);
      } catch {
        // ignore
      }
    };

    const buildBackPath = () => {
      const q = new URLSearchParams();
      q.set("tab", activeTab.value);
      const rn = String(receiptNo.value || "").trim();
      const sn = String(sendNo.value || "").trim();
      if (rn) q.set("receiptNo", rn);
      if (sn) q.set("sendNo", sn);
      return `/pls-receive?${q.toString()}`;
    };

    // 处理返回：回首页并清空缓存单号
    const handleBack = () => {
      clearScanCache();
      receiptNo.value = "";
      receiptData.value = null;
      materialList.value = [];
      sendNo.value = "";
      gvList.value = [];
      selectedGvList.value = [];
      router.replace("/home");
    };

    // 切换 tab
    const switchTab = (tab) => {
      activeTab.value = tab;
      nextTick(() => {
        if (tab === "gv" && scanInput.value) {
          scanInput.value.focus();
        } else if (tab === "send" && sendScanInput.value) {
          sendScanInput.value.focus();
        }
      });
    };

    // 发运单扫描处理
    const handleSendScan = () => {
      sendNo.value = sendNo.value.replace(/\s+/g, "").trim();
      saveScanCache();
    };

    // 清空发运单输入
    const clearSendInput = () => {
      sendNo.value = "";
      gvList.value = [];
      selectedGvList.value = [];
      saveScanCache();
    };

    // 查询发运单数据
    const querySendData = async () => {
      if (!sendNo.value.trim()) {
        toast.error("请输入发运单号");
        return;
      }

      isLoading.value = true;
      toast.default("正在查询收货单...");

      try {
        const response = await request.get(
          "uic-system_hf/wareindetail/pls/page",
          {
            params: {
              source: 1,
              sendNo: sendNo.value.trim(),
              wareInStatus: 1,
              current: 1,
              size: 100,
            },
          },
        );

        if (response.code === 200 && response.data) {
          const data = response.data;
          if (data.records && data.records.length > 0) {
            gvList.value = data.records;
            selectedGvList.value = [];
            saveScanCache();
            toast.success(`查询成功，共 ${data.records.length} 个GV单`);
          } else {
            toast.error("未找到收货单信息");
            gvList.value = [];
            selectedGvList.value = [];
          }
        } else {
          toast.error("查询失败");
        }
      } catch (error) {
        console.error("查询失败:", error);
        toast.error("网络异常，请稍后重试");
      } finally {
        isLoading.value = false;
      }
    };

    // 判断是否全选
    const isAllSelected = computed(() => {
      if (gvList.value.length === 0) return false;
      return selectedGvList.value.length === gvList.value.length;
    });

    // 计算选中GV单的入库数量汇总
    const selectedTotalQty = computed(() => {
      return selectedGvList.value.reduce((sum, item) => {
        return sum + (Number(item.wareInNum) || 0);
      }, 0);
    });

    // 全选/取消全选
    const toggleSelectAll = () => {
      if (isAllSelected.value) {
        selectedGvList.value = [];
      } else {
        selectedGvList.value = [...gvList.value];
      }
    };

    // 判断GV单是否已选中
    const isGvSelected = (item) => {
      return selectedGvList.value.some(
        (selected) =>
          (selected.id && selected.id === item.id) ||
          (selected.wareInId && selected.wareInId === item.wareInId),
      );
    };

    // 切换GV单选择状态
    const toggleGvSelect = (item) => {
      const index = selectedGvList.value.findIndex(
        (selected) =>
          (selected.id && selected.id === item.id) ||
          (selected.wareInId && selected.wareInId === item.wareInId),
      );

      if (index > -1) {
        selectedGvList.value.splice(index, 1);
      } else {
        selectedGvList.value.push(item);
      }
    };

    // 扫描处理
    const handleScan = () => {
      receiptNo.value = receiptNo.value.replace(/\s+/g, "").trim();
      saveScanCache();
    };

    // 清空输入
    const clearInput = () => {
      receiptNo.value = "";
      receiptData.value = null;
      materialList.value = [];
      saveScanCache();
    };

    // 查询收货单数据
    const queryData = async () => {
      if (!receiptNo.value.trim()) {
        toast.error("请输入PDA单号");
        return;
      }

      isLoading.value = true;
      toast.default("正在查询收货单...");

      try {
        const response = await request.get(
          "/uic-system_hf/wareindetail/pls/page",
          {
            params: {
              source: 1,
              receiptNo: receiptNo.value.trim(),
              wareInStatus: 1,
              current: 1,
              size: 20,
            },
          },
        );

        if (response.code === 200 && response.data) {
          const data = response.data;
          if (data.records && data.records.length > 0) {
            receiptData.value = data.records[0];
            materialList.value = [data.records[0]];
            saveScanCache();
            toast.success("查询成功");
          } else {
            toast.error("未找到收货单信息");
            receiptData.value = null;
            materialList.value = [];
          }
        } else {
          toast.error("查询失败");
        }
      } catch (error) {
        console.error("查询失败:", error);
        toast.error("网络异常，请稍后重试");
      } finally {
        isLoading.value = false;
      }
    };

    // 开始收货
    const startReceipt = async () => {
      isSubmitting.value = true;
      toast.default("正在加载收货详情...");

      try {
        // 离开前缓存扫码单号，返回后自动回填并查询
        saveScanCache();

        if (activeTab.value === "gv") {
          // 扫GV单模式 - 单个收货
          if (!receiptData.value) return;

          const wareInId = receiptData.value.wareInId || receiptData.value.id;
          if (!wareInId) {
            throw new Error("缺少入库单ID");
          }

          // 先调用detail接口验证数据
          const detailResponse = await request.get(
            "/uic-system/api_hf/warein/detail",
            {
              params: {
                id: wareInId,
              },
            },
          );

          if (detailResponse.code !== 200 || !detailResponse.data) {
            throw new Error("获取收货详情失败");
          }

          // 跳转到WareInReceiveSingle收货页面
          await router.push({
            path: "/warein-receive/single",
            query: {
              receiptId: wareInId,
              back: buildBackPath(),
            },
          });
        } else {
          // 扫发运单模式 - 批量收货
          if (selectedGvList.value.length === 0) {
            throw new Error("请至少选择一个GV单");
          }

          // 跳转到批量收货页面
          await router.push({
            path: "/warein-batch-receive",
            query: {
              selectedList: JSON.stringify(selectedGvList.value),
              back: buildBackPath(),
              source: "pls",
            },
          });
        }
      } catch (error) {
        console.error("跳转失败:", error);
        toast.error(error.message || "跳转失败");
      } finally {
        isSubmitting.value = false;
      }
    };

    // 获取状态文本
    const getStatusText = (status) => {
      switch (status) {
        case 0:
          return "待分配";
        case 1:
          return "待收货";
        case 3:
          return "已完成";
        case 6:
          return "正在入库中";
        default:
          return "异常";
      }
    };

    // 获取状态样式
    const getStatusClass = (status) => {
      switch (status) {
        case 0:
          return "status-pending";
        case 1:
          return "status-receiving";
        case 3:
          return "status-completed";
        case 6:
          return "status-processing";
        default:
          return "status-error";
      }
    };

    // 获取状态Tag类型
    const getStatusTagType = (status) => {
      switch (status) {
        case 0:
          return "info";
        case 1:
          return "";
        case 3:
          return "success";
        case 6:
          return "warning";
        default:
          return "danger";
      }
    };

    // 格式化数字
    const formatNumber = (num) => {
      return Number(num).toFixed(3);
    };

    // 启动扫码
    const startScan = () => {
      scanVisible.value = true;
    };

    // 处理扫码成功
    const handleScanSuccess = (decodedText) => {
      const text = String(decodedText || "")
        .replace(/[\u200B-\u200D\uFEFF]/g, "")
        .replace(/[\r\n\t]/g, "")
        .trim();

      if (!text) return;

      if (activeTab.value === "gv") {
        receiptNo.value = text;
        handleScan();
        queryData();
      } else {
        sendNo.value = text;
        handleSendScan();
        querySendData();
      }

      toast.success("扫码成功");
      scanVisible.value = false;
    };

    // 恢复缓存并自动查询
    const restoreScanCacheAndQuery = async () => {
      let tab = String(route.query?.tab || "").trim();
      let rn = String(route.query?.receiptNo || "").trim();
      let sn = String(route.query?.sendNo || "").trim();

      // 路由参数优先，其次读 sessionStorage（兼容返回键）
      if (!tab && !rn && !sn) {
        try {
          const raw = sessionStorage.getItem(PLS_RECEIVE_CACHE_KEY);
          if (raw) {
            const cache = JSON.parse(raw);
            tab = String(cache?.activeTab || "").trim();
            rn = String(cache?.receiptNo || "").trim();
            sn = String(cache?.sendNo || "").trim();
          }
        } catch {
          // ignore
        }
      }

      if (tab === "gv" || tab === "send") {
        activeTab.value = tab;
      }
      if (rn) receiptNo.value = rn;
      if (sn) sendNo.value = sn;
      saveScanCache();

      await nextTick();
      if (activeTab.value === "gv" && receiptNo.value.trim()) {
        await queryData();
      } else if (activeTab.value === "send" && sendNo.value.trim()) {
        await querySendData();
      }
    };

    // 自动聚焦 + 返回后回填单号并自动查询
    onMounted(() => {
      nextTick(async () => {
        await restoreScanCacheAndQuery();
        if (activeTab.value === "gv" && scanInput.value) {
          scanInput.value.focus();
        } else if (activeTab.value === "send" && sendScanInput.value) {
          sendScanInput.value.focus();
        }
      });
    });

    // 离开到首页/其他菜单时清缓存；去收货页则保留
    onBeforeRouteLeave((to) => {
      const path = String(to?.path || "");
      const keep =
        path.includes("/warein-batch-receive") ||
        path.includes("/warein-receive/single");
      if (!keep) {
        clearScanCache();
      }
    });

    return {
      activeTab,
      tabOptions,
      handleTabChange,
      receiptNo,
      receiptData,
      materialList,
      sendNo,
      gvList,
      selectedGvList,
      selectedTotalQty,
      isLoading,
      isSubmitting,
      scanInput,
      sendScanInput,
      scanVisible,
      handleBack,
      switchTab,
      handleScan,
      clearInput,
      queryData,
      handleSendScan,
      clearSendInput,
      querySendData,
      isGvSelected,
      toggleGvSelect,
      isAllSelected,
      toggleSelectAll,
      startReceipt,
      startScan,
      handleScanSuccess,
      getStatusText,
      getStatusClass,
      getStatusTagType,
      formatNumber,
    };
  },
};
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.pls-receive-container {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--background-color);
  display: flex;
  flex-direction: column;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  overflow: hidden;
}

.header {
  display: flex;
  align-items: center;
  padding: 0 12px;
  padding-top: env(safe-area-inset-top);
  background-color: var(--header-background);
  color: var(--header-text);
  box-shadow: 0 2px 8px var(--shadow-light);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  height: calc(52px + env(safe-area-inset-top));
}

.back-btn {
  width: 36px;
  height: 36px;
}

.icon-back {
  width: 20px;
  height: 20px;
}

.title {
  flex: 1;
  text-align: center;
  font-size: 16px;
  font-weight: 600;
  color: var(--header-text);
}

.tab-segment {
  margin-top: calc(52px + env(safe-area-inset-top));
  padding: 8px 10px;
}

.tab-segment :deep(.el-segmented) {
  border-radius: 12px;
  padding: 3px;
  background-color: #e8f4f8;
}

.tab-segment :deep(.el-segmented__item.is-selected) {
  background-color: #ffffff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.tab-segment :deep(.el-segmented__item) {
  border-radius: 10px;
  padding: 8px 0;
  margin: 0;
}

.content {
  flex: 1;
  padding: 8px 10px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.send-tab-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.scan-section {
  margin-bottom: 8px;
}

.scan-box {
  background: var(--card-background);
  border-radius: 10px;
  padding: 10px;
  box-shadow: 0 2px 8px var(--shadow-light);
  margin-bottom: 8px;
}

.scan-input :deep(.el-input__wrapper) {
  border-radius: 8px;
  background: var(--background-color);
}

.scan-input :deep(.el-input__wrapper.is-focus) {
  box-shadow: 0 0 0 1px var(--primary-color) inset;
}

.scan-actions {
  display: flex;
  gap: 8px;
  justify-content: center;
}

.scan-actions .el-button {
  flex: 1;
}

.light-btn.el-button--primary {
  --el-button-bg-color: #5bc0de !important;
  --el-button-border-color: #5bc0de !important;
  --el-button-hover-bg-color: #46b5d5 !important;
  --el-button-hover-border-color: #46b5d5 !important;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 8px 0;
}

.section-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 6px;
}

.select-hint {
  font-size: 12px;
  color: var(--text-secondary);
}

.section-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.sticky-header {
  position: sticky;
  top: 0;
  z-index: 10;
  background: var(--card-background);
  padding: 8px 10px;
  border-radius: 10px 10px 0 0;
  box-shadow: 0 2px 4px var(--shadow-light);
}

.gv-list-section {
  margin-bottom: 10px;
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.supplier-header {
  background: var(--card-background);
  border-radius: 10px;
  padding: 8px 12px;
  margin-bottom: 6px;
  box-shadow: 0 2px 8px var(--shadow-light);
  display: flex;
  align-items: center;
}

.supplier-label {
  font-size: 13px;
  color: var(--text-secondary);
  margin-right: 4px;
}

.supplier-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
}

.gv-list-wrapper {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  background: var(--card-background);
  border-radius: 10px;
  box-shadow: 0 2px 8px var(--shadow-light);
}

.gv-list {
  background: transparent;
}

.gv-item {
  display: flex;
  align-items: center;
  padding: 8px 10px;
  border-bottom: 1px solid var(--border-color);
  cursor: pointer;
  transition: background-color 0.2s;
  gap: 8px;
}

.gv-item:last-child {
  border-bottom: none;
}

.gv-item:active {
  background-color: rgba(0, 0, 0, 0.03);
}

.gv-content {
  flex: 1;
  min-width: 0;
}

.gv-row {
  display: flex;
  justify-content: space-between;
  padding: 2px 0;
}

.gv-label {
  font-size: 12px;
  color: var(--text-secondary);
}

.gv-value {
  font-size: 12px;
  font-weight: 500;
  color: var(--text-primary);
}

.gv-value.text-primary {
  color: var(--primary-color);
  font-weight: 600;
}

.gv-row-fail {
  align-items: flex-start;
  background: rgba(239, 68, 68, 0.08);
  padding: 4px;
  border-radius: 4px;
  margin-top: 4px;
}

.gv-value-fail {
  color: var(--danger-color, var(--error-color));
  font-weight: 400;
  font-size: 11px;
}

.info-card,
.material-list {
  background: var(--card-background);
  border-radius: 10px;
  padding: 10px;
  box-shadow: 0 2px 8px var(--shadow-light);
}

.receipt-info {
  margin-bottom: 8px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px solid var(--border-color);
}

.info-row:last-child {
  border-bottom: none;
}

.label {
  font-size: 13px;
  color: var(--text-secondary);
}

.value {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
}

.info-row-fail {
  align-items: flex-start;
}

.value.value-fail {
  color: var(--danger-color, var(--error-color));
  background: rgba(239, 68, 68, 0.08);
  border-left: 3px solid var(--danger-color, var(--error-color));
  padding: 4px 6px;
  border-radius: 4px;
  display: block;
  white-space: normal;
  word-break: break-all;
  text-align: right;
  line-height: 1.4;
  font-size: 12px;
}

.value.status-processing {
  color: var(--secondary-color);
}

.value.status-error {
  color: var(--error-color);
}

.material-section {
  margin-bottom: 8px;
}

.material-item {
  padding: 8px 0;
  border-bottom: 1px solid var(--border-color);
}

.material-item:last-child {
  border: none;
}

.material-header {
  margin-bottom: 4px;
}

.material-no {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
}

.detail-row {
  display: flex;
  justify-content: space-between;
  padding: 2px 0;
}

.detail-label {
  font-size: 12px;
  color: var(--text-secondary);
}

.detail-value {
  font-size: 12px;
  color: var(--text-primary);
}

.detail-value.text-primary {
  color: var(--primary-color);
  font-weight: 600;
}

.fixed-bottom-action {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: var(--card-background);
  padding: 10px 12px;
  padding-bottom: calc(10px + env(safe-area-inset-bottom));
  box-shadow: 0 -2px 10px var(--shadow-light);
  z-index: 100;
}

.start-btn {
  width: 100%;
  height: 42px;
  font-size: 15px;
  font-weight: 600;
  --el-button-bg-color: #6ecf8e !important;
  --el-button-border-color: #6ecf8e !important;
  --el-button-hover-bg-color: #5cc47e !important;
  --el-button-hover-border-color: #5cc47e !important;
}

/* 扫码按钮样式 */
.scan-input :deep(.el-input__suffix) {
  pointer-events: auto;
}

.scan-btn {
  padding: 0;
  margin-right: 4px;
  color: var(--primary-color);
}

.scan-btn:hover {
  color: var(--primary-color);
}
</style>
